import { Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import cross from '~/assets/icons/cross.svg';
import info from '~/assets/icons/info_2.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections';
import { useWalletIcon, useZoomFactor } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { Currency, TechTuningItemProperty } from '~/shared';
import { ImgIcon } from '../ImgIcon';
import { ShopTuningShopHint } from '../ShopTuningShopHint';
import { Text } from '../Text';
import {
	StyledCategory,
	StyledContainer,
	StyledCount,
	StyledCrossButton,
	StyledDragHintContainer,
	StyledImageBg,
	StyledImageContainer,
	StyledInfoButton,
	StyledInfoContainer,
	StyledNewDot,
	StyledNewShadow,
	StyledOldPriceContainer,
	StyledPriceContainer,
	StyledPricesContainer,
	StyledTitle,
} from './ShopTuningShopItem.styled';

let dragHintOwnerId: string | null = null;

interface IShopTuningShopItemProps {
	itemId: string;
	title: string;
	category: string;
	price: number;
	oldPrice?: number;
	discount?: number;
	currency: Currency;
	active: boolean;
	isNew: boolean;
	image: string;
	imageBg?: string;
	handleClick: () => void;
	onShiftClick?: () => void;
	onDragStart?: (e: React.DragEvent) => void;
	onDragEnd?: () => void;
	dragging?: boolean;
	owned?: boolean;
	isDraggable?: boolean;
	count?: number;
	properties?: Record<TechTuningItemProperty, number | null>;
	dragHint?: boolean;
}

const ShopTuningShopItem = ({
	itemId,
	title,
	category,
	price,
	oldPrice,
	currency,
	active,
	isNew,
	image,
	imageBg,
	handleClick,
	onShiftClick,
	onDragStart,
	onDragEnd,
	isDraggable,
	owned,
	count,
	properties,
	dragHint,
	...props
}: IShopTuningShopItemProps & React.HTMLAttributes<HTMLDivElement>) => {
	const [hintVisible, setHintVisible] = useState(false);
	const [hintPosition, setHintPosition] = useState<{
		top: number;
		left: number;
	}>({ top: 0, left: 0 });
	const [dragHintVisible, setDragHintVisible] = useState(false);
	const dragHintDismissed = useObservable(
		rage.player.getTechTuningDragHintShowed(),
	);
	const containerRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();

	useEffect(() => {
		return () => {
			if (dragHintOwnerId === itemId) {
				dragHintOwnerId = null;
			}
		};
	}, [itemId]);

	const showHint = () => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			setHintPosition({
				top:
					rect.top * (1 / zoomFactor) +
					(rect.height * (1 / zoomFactor) - 80) / 2,
				left: rect.right * (1 / zoomFactor) + 10,
			});
		}
		setHintVisible(true);
	};

	const hideHint = () => {
		setHintVisible(false);
	};

	const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
		if (props.onMouseEnter) {
			props.onMouseEnter(e);
		}

		if (!dragHint || dragHintDismissed || !owned || !isDraggable) {
			return;
		}

		if (dragHintOwnerId === null) {
			dragHintOwnerId = itemId;
		}

		if (dragHintOwnerId === itemId) {
			setDragHintVisible(true);
		}
	};

	const hideDragHint = () => {
		setDragHintVisible(false);
	};

	const currencyIcon = useWalletIcon(currency, true);

	const handleDragStart = (e: React.DragEvent) => {
		if (dragHintVisible) return;
		if (!isDraggable) return;

		hideHint();
		hideDragHint();

		if (e.dataTransfer.setDragImage) {
			const baseSize = 60;
			const scaledSize = baseSize * zoomFactor;
			const background = imageBg || 'rgba(255, 255, 255, 0.02)';

			const dragImage = document.createElement('div');
			dragImage.className = 'drag-image';
			dragImage.style.width = `${scaledSize}px`;
			dragImage.style.height = `${scaledSize}px`;
			dragImage.style.background = background;

			document.body.appendChild(dragImage);

			const imageSize = 40 * zoomFactor;

			if (image) {
				const img = document.createElement('img');
				img.src = image;
				img.className = 'drag-image-icon';
				img.style.width = `${imageSize}px`;
				img.style.height = `${imageSize}px`;
				dragImage.appendChild(img);
			}

			e.dataTransfer.setDragImage(dragImage, scaledSize / 2, scaledSize / 2);

			setTimeout(() => {
				document.body.removeChild(dragImage);
			}, 0);
		}
		onDragStart?.(e);
	};

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					in={hintVisible}
					classNames={'hint'}
					timeout={300}
					unmountOnExit
				>
					<ShopTuningShopHint
						name={title}
						properties={properties}
						style={{
							top: hintPosition.top,
							left: hintPosition.left,
							position: 'fixed',
						}}
					/>
				</CSSTransition>,
				document.body,
			)}
			<StyledContainer
				ref={containerRef}
				$invertCurrency={currency !== Currency.Donate}
				$active={active}
				$draggable={isDraggable}
				$dragHintVisible={!!dragHint && !dragHintDismissed && dragHintVisible}
				draggable={isDraggable}
				onClick={(e) => {
					if (e.shiftKey && onShiftClick) {
						onShiftClick();
						return;
					}
					handleClick();
				}}
				onDragStart={handleDragStart}
				onDragEnd={onDragEnd}
				onMouseEnter={handleMouseEnter}
				id={`techTuning-${itemId}`}
				{...props}
			>
				<StyledImageContainer>
					<StyledImageBg style={{ background: imageBg }} />
					<img
						src={image}
						style={{ position: 'relative' }}
						width={60}
						height={60}
					/>
					{isNew && !owned && (
						<Fragment>
							<StyledNewShadow />
							<StyledNewDot />
						</Fragment>
					)}
				</StyledImageContainer>
				{count && count > 1 && (
					<StyledCount>
						<Text>x{count}</Text>
					</StyledCount>
				)}
				<StyledInfoContainer>
					<div>
						<StyledCategory>{category}</StyledCategory>
						<StyledTitle>{title}</StyledTitle>
						<StyledPricesContainer>
							<StyledPriceContainer>
								<CurrencyIcon icon={currencyIcon} size="sm" />
								<Text>{price.toLocaleString('ru-RU')}</Text>
							</StyledPriceContainer>
							{oldPrice ? (
								<StyledOldPriceContainer>
									<CurrencyIcon icon={currencyIcon} size="sm" />
									<Text>{oldPrice.toLocaleString('ru-RU')}</Text>
									<div className="line"></div>
								</StyledOldPriceContainer>
							) : null}
						</StyledPricesContainer>
					</div>
					<StyledInfoButton onMouseEnter={showHint} onMouseLeave={hideHint}>
						<img src={info} />
					</StyledInfoButton>
				</StyledInfoContainer>
				<CSSTransition
					in={!!dragHint && !dragHintDismissed && dragHintVisible}
					classNames={'hint'}
					timeout={300}
					unmountOnExit
				>
					<StyledDragHintContainer
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
					>
						<ImgIcon icon={info} width={20} height={20} color="#FABE2C" />
						<Text style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
							Удерживайте деталь, чтобы
							<br />
							перенести её в свою ячейку
						</Text>
						<StyledCrossButton
							onMouseEnter={() => playSound(Sound.LinkCursor)}
							onClick={() => {
								playSound(Sound.ClickButton);
								setDragHintVisible(false);
								void rage.player
									.setTechTuningDragHintShowed(true)
									.catch(() => undefined);
							}}
						>
							<img src={cross} />
						</StyledCrossButton>
					</StyledDragHintContainer>
				</CSSTransition>
			</StyledContainer>
		</Fragment>
	);
};

export { ShopTuningShopItem };
